import React, { useState } from 'react';
import { Send, MapPin, CheckCircle, AlertOctagon, FileText } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getAuth } from 'firebase/auth';

const agencies = {
    "middle_east": "Middle East (Gulf Talent, NaukriGulf...)",
    "us_tech": "US Tech Sector (Tech Systems, CyberCoders...)",
    "europe_finance": "Europe Finance (Michael Page, Hays...)",
    "apac_general": "APAC General (Adecco, Randstad...)"
};

const RecruitmentBlast = ({ onBack }) => {
    const { hasFeature, purchaseFeature, getFeaturePrice } = useSubscription();
    const [region, setRegion] = useState('');
    const [resumeLink, setResumeLink] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const price = getFeaturePrice('recruitment_blast') || 15; // Default $15
    const isUnlocked = hasFeature('recruitment_blast') || hasFeature('all_access');

    const handleBlast = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResult(null);

        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            alert("Please login first.");
            setLoading(false);
            return;
        }

        try {
            const token = await user.getIdToken();
            const response = await fetch('/api/blast-resume', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    region,
                    resumeLink,
                    message
                })
            });

            const data = await response.json();
            if (response.ok) {
                setResult({ success: true, message: data.message });
            } else {
                setResult({ success: false, message: data.error });
            }
        } catch (err) {
            setResult({ success: false, message: "Network error: " + err.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 p-4 md:p-8">
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-700 to-indigo-800 p-8 text-white relative">
                    <div className="flex justify-between items-start relative z-10">
                        <div>
                            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                                <Send className="animate-pulse" /> Recruitment Blast
                            </h1>
                            <p className="opacity-90">Instantly distribute your resume to top recruitment agencies in your target region.</p>
                        </div>
                        <button onClick={onBack} className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg text-sm font-bold backdrop-blur-sm transition-all">
                            Back
                        </button>
                    </div>
                    {/* Bg Decoration */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                </div>

                <div className="p-8">
                    {!isUnlocked ? (
                        <div className="text-center py-10">
                            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-purple-600">
                                <Send size={32} />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-800 mb-2">Unlock Global Reach</h2>
                            <p className="text-slate-600 mb-6 max-w-md mx-auto">
                                Get your resume in front of hundreds of recruiters with one click. Select a region and let our automated system handle the outreach.
                            </p>
                            <div className="flex justify-center gap-4">
                                <button
                                    onClick={() => purchaseFeature('recruitment_blast', price)}
                                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-bold text-lg shadow-lg hover:shadow-purple-500/20 transition-all flex items-center gap-2"
                                >
                                    Unlock Blast (${price})
                                </button>
                                <button
                                    onClick={() => purchaseFeature('all_access')}
                                    className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3 rounded-xl font-bold text-lg shadow-lg transition-all"
                                >
                                    Get All Access
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleBlast} className="space-y-6">

                            {/* Region Selection */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                                    <MapPin size={16} className="text-purple-600" /> Target Region
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {Object.entries(agencies).map(([key, label]) => (
                                        <label key={key} className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${region === key ? 'border-purple-600 bg-purple-50 ring-1 ring-purple-600' : 'border-slate-200 hover:border-purple-300'}`}>
                                            <input
                                                type="radio"
                                                name="region"
                                                value={key}
                                                checked={region === key}
                                                onChange={e => setRegion(e.target.value)}
                                                className="sr-only"
                                            />
                                            <div className="font-bold text-slate-800">{key.replace('_', ' ').toUpperCase()}</div>
                                            <div className="text-xs text-slate-500 mt-1">{label}</div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Resume Link */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                                    <FileText size={16} className="text-purple-600" /> Resume Link
                                </label>
                                <input
                                    type="url"
                                    required
                                    placeholder="https://docs.google.com/document/d/..."
                                    value={resumeLink}
                                    onChange={e => setResumeLink(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-purple-600 outline-none"
                                />
                                <p className="text-xs text-slate-500 mt-2"><strong>Tip:</strong> Provide a public Google Doc or PDF link. We currently do not support direct file uploads for the blast.</p>
                            </div>

                            {/* Custom Message */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Message to Recrutiers (Optional)</label>
                                <textarea
                                    rows="4"
                                    placeholder="I am an experienced engineer looking for..."
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-purple-600 outline-none"
                                />
                            </div>

                            {result && (
                                <div className={`p-4 rounded-xl flex items-start gap-3 ${result.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                    {result.success ? <CheckCircle className="shrink-0" /> : <AlertOctagon className="shrink-0" />}
                                    <div>
                                        <p className="font-bold">{result.success ? 'Blast Successful!' : 'Blast Failed'}</p>
                                        <p className="text-sm">{result.message}</p>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading || !region || !resumeLink}
                                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-4 rounded-xl text-lg shadow-lg shadow-purple-500/30 transition-all disabled:opacity-50 disabled:shadow-none"
                            >
                                {loading ? 'Sending Blasts...' : 'Launch Campaign 🚀'}
                            </button>

                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecruitmentBlast;
